
  import { Component, Vue, Watch } from 'vue-property-decorator'
  import { mapActions, mapGetters } from 'vuex'
  import { Period, periods, IPeriod } from '@/store/resources/period'
  import dayjs from 'dayjs'

@Component({
  methods: {
    ...mapActions('resources', ['setPeriod']),
  },
  computed: {
    periods: () => periods,
    ...mapGetters('resources', ['period']),
  },
})
  export default class PeriodSelector extends Vue {
  private period!: Period
  private periods!: Array<Period>
  private setPeriod!: (period: Period) => void

  // Estado para selección de fechas
  private showDatePicker = false
  private selectedPeriod: Period | null = null

  // Variables para rango de fechas
  private fromDate: string | null = null
  private toDate: string | null = null
  private fromMenu = false
  private toMenu = false

  // Variables para día exacto
  private exactDate: string | null = null
  private dayMenu = false

  // Formateo de fechas para mostrar
  get fromDateFormatted (): string {
    return this.fromDate ? this.formatDate(this.fromDate) : ''
  }

  get toDateFormatted (): string {
    return this.toDate ? this.formatDate(this.toDate) : ''
  }

  get exactDateFormatted (): string {
    return this.exactDate ? this.formatDate(this.exactDate) : ''
  }

  // Método utilitario para actualizar URL preservando el parámetro search
  updateUrlParams (updates: Record<string, any>, removals: string[] = []): void {
    // Copiar todos los parámetros actuales
    const query = { ...this.$route.query }

    // Aplicar actualizaciones
    Object.entries(updates).forEach(([key, value]) => {
      query[key] = value
    })

    // Aplicar eliminaciones (excepto 'search')
    removals.forEach(key => {
      if (key !== 'search' && query[key]) {
        delete query[key]
      }
    })

    // Actualizar URL
    this.$router.replace({ query }).catch(() => {})
  }

  mounted () {
    const { $route: { query } } = this
    const { period, exactDate, fromDate, toDate } = query

    let currentPeriod = null

    if (period) {
      currentPeriod = periods.find(p => p.name === period)

      // Si es un período personalizado y tenemos fechas en la URL, usarlas
      if (currentPeriod && currentPeriod.allowDateSelection) {
        const periodConfig: IPeriod = {
          name: currentPeriod.name,
          title: currentPeriod.title,
          type: currentPeriod.type,
          allowDateSelection: currentPeriod.allowDateSelection,
          field: 'created_at',
        }

        if (currentPeriod.name === 'day' && exactDate) {
          // Restaurar día exacto desde URL
          periodConfig.from = dayjs(exactDate as string)
          this.exactDate = exactDate as string
        } else if (currentPeriod.name === 'range' && fromDate && toDate) {
          // Restaurar rango de fechas desde URL
          periodConfig.from = dayjs(fromDate as string)
          periodConfig.to = dayjs(toDate as string)
          this.fromDate = fromDate as string
          this.toDate = toDate as string
        }

        currentPeriod = new Period(periodConfig)
      }

      if (currentPeriod) this.setPeriod(currentPeriod)
    } else if (this.$route.query.search) {
      // Si hay búsqueda pero no periodo, aplicamos periodo por defecto
      const defaultPeriod = periods.find(p => p.name === 'last_12_months')
      if (defaultPeriod) this.setPeriod(defaultPeriod)
    }
  }

  formatDate (date: string): string {
    return dayjs(date).format('DD/MM/YYYY')
  }

  getCurrentDate (): string {
    return dayjs().format('YYYY-MM-DD')
  }

  confirmFromDate () {
    this.fromMenu = false
  }

  confirmToDate () {
    this.toMenu = false
  }

  confirmExactDate () {
    this.dayMenu = false
  }

  onPeriodChange (newPeriod: Period) {
    if (newPeriod && newPeriod.allowDateSelection) {
      // Guardar el período seleccionado para referencia
      this.selectedPeriod = newPeriod

      // Inicializar fechas por defecto
      if (newPeriod.name === 'day') {
        this.exactDate = this.getCurrentDate()
      } else if (newPeriod.name === 'range') {
        this.fromDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD')
        this.toDate = this.getCurrentDate()
      }

      // Mostrar el diálogo de selección de fechas
      this.showDatePicker = true
    } else {
      // Para períodos normales, simplemente aplicar
      this.setPeriod(newPeriod)

      // Actualizar URL - Aquí SÍ eliminamos search si es necesario cambiarlo
      this.updateUrlParams(
        { period: newPeriod.name },
        ['exactDate', 'fromDate', 'toDate']
      )
    }
  }

  cancelDateSelection () {
    this.showDatePicker = false
    this.selectedPeriod = null
  }

  applyDateSelection () {
    if (!this.selectedPeriod) return

    // Crear un objeto de tipo IPeriod
    const periodConfig: IPeriod = {
      name: this.selectedPeriod.name,
      title: this.selectedPeriod.title,
      type: this.selectedPeriod.type,
      allowDateSelection: this.selectedPeriod.allowDateSelection,
      field: 'created_at',
    }

    // Parámetros a actualizar
    const updates: Record<string, any> = {
      period: this.selectedPeriod.name,
    }

    // Parámetros a eliminar
    const removals: string[] = []

    if (this.selectedPeriod.name === 'day' && this.exactDate) {
      // Configurar día exacto
      periodConfig.from = dayjs(this.exactDate)

      // Actualizar parámetros
      updates.exactDate = this.exactDate
      removals.push('fromDate', 'toDate')
    } else if (this.selectedPeriod.name === 'range' && this.fromDate && this.toDate) {
      // Configurar rango de fechas
      periodConfig.from = dayjs(this.fromDate)
      periodConfig.to = dayjs(this.toDate)

      // Actualizar parámetros
      updates.fromDate = this.fromDate
      updates.toDate = this.toDate
      removals.push('exactDate')
    } else {
      // Para periodos normales
      removals.push('exactDate', 'fromDate', 'toDate')
    }

    // Actualizar URL (nunca elimina 'search')
    this.updateUrlParams(updates, removals)

    // Aplicar el período con las fechas seleccionadas
    this.setPeriod(new Period(periodConfig))
    this.showDatePicker = false
  }

  @Watch('period', { immediate: true })
  onPeriodUpdate (val, prev) {
    if (!val) return

    // Solo actualizamos period en la URL si ha cambiado
    if ((val?.name && !this.$route.query.period) ||
        (this.$route.query.period !== val.name && prev)) {
      // Actualizamos solo el parámetro period sin tocar otros
      this.updateUrlParams({ period: val.name })
    }
  }
  }
